import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  Clock3,
  Edit,
  Mail,
  MapPin,
  Phone,
  RefreshCw,
  Tag,
  User,
} from 'lucide-react';
import { toast } from 'sonner';

import { useAuth } from '../../contexts/AuthContext';
import { getLeadDetails } from '../../lib/api';
import { ScheduleFollowUpModal, StatusBadge } from '../../components/ui/ActionModals';
import { FollowUpCompleteDialog, FollowUpEditDialog } from '../../components/common/LeadFollowUpViews';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';

const priorityStyles = {
  High: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  Medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  Low: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
};

function fmtDateTime(value) {
  if (!value) return { date: '-', time: '-' };
  const dt = new Date(value);
  return {
    date: dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
    time: dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }),
  };
}

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(amount) || 0);
}

function getLeadDisplayStatus(status) {
  if (status === 'follow_up') return 'Contacted';
  if (status === 'converted') return 'Converted';
  if (status === 'rejected') return 'Rejected';
  return 'Pending';
}

function getRoleBasePath(pathname) {
  const [, role] = pathname.split('/');
  return role ? `/${role}` : '';
}

function InfoBlock({ label, value }) {
  return (
    <div className="space-y-1">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-sm font-medium break-words">{value || '-'}</p>
    </div>
  );
}

export default function LeadDetailsPage() {
  const { leadId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [completingItem, setCompletingItem] = useState(null);
  const [showAddFollowUp, setShowAddFollowUp] = useState(false);

  const roleBasePath = useMemo(() => getRoleBasePath(location.pathname), [location.pathname]);

  const fetchLead = useCallback(async () => {
    if (!token || !leadId) return;
    setLoading(true);
    try {
      const resp = await getLeadDetails(token, leadId);
      setData(resp?.data || null);
    } catch (err) {
      setData(null);
      toast.error(err?.message || 'Failed to load lead details');
    } finally {
      setLoading(false);
    }
  }, [leadId, token]);

  useEffect(() => {
    fetchLead();
  }, [fetchLead]);

  const lead = data?.lead;
  const followUps = data?.follow_ups || [];
  const currentFollowUp = data?.current_follow_up || null;
  const timeline = data?.timeline || [];
  const scheduleLead = lead
    ? { id: lead.id, name: lead.customer_name || lead.full_name || lead.name }
    : null;

  return (
    <div className="space-y-6">
      <FollowUpEditDialog item={editingItem} open={Boolean(editingItem)} onClose={() => setEditingItem(null)} onSaved={fetchLead} />
      <FollowUpCompleteDialog item={completingItem} open={Boolean(completingItem)} onClose={() => setCompletingItem(null)} onSaved={fetchLead} />
      <ScheduleFollowUpModal
        lead={scheduleLead}
        open={showAddFollowUp}
        onClose={() => setShowAddFollowUp(false)}
        token={token}
        onSaved={fetchLead}
      />

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-3">
          <Button variant="ghost" className="w-fit px-0 text-muted-foreground" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">
                {lead?.customer_name || 'Lead Details'}
              </h1>
              {lead ? <StatusBadge status={getLeadDisplayStatus(lead.status)} /> : null}
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{lead?.id || leadId}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" asChild>
            <Link to={`${roleBasePath}/leads/followup`}>
              <CalendarDays className="mr-2 h-4 w-4" />
              Follow-Up Leads
            </Link>
          </Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setShowAddFollowUp(true)} disabled={!lead}>
            <CalendarDays className="mr-2 h-4 w-4" />
            Add Follow-Up
          </Button>
        </div>
      </div>

      {loading ? (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground">Loading lead details...</CardContent>
        </Card>
      ) : !lead ? (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground">Lead details not available.</CardContent>
        </Card>
      ) : (
        <>
          <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
            <Card className="border border-border">
              <CardHeader>
                <CardTitle>Lead Information</CardTitle>
                <CardDescription>Core lead data from the current CRM record</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                <InfoBlock label="Lead ID" value={lead.id} />
                <InfoBlock label="Full Name" value={lead.customer_name} />
                <InfoBlock label="Phone" value={lead.phone} />
                <InfoBlock label="Email" value={lead.email} />
                <InfoBlock label="Status" value={getLeadDisplayStatus(lead.status)} />
                <InfoBlock label="Priority" value={lead.priority} />
                <InfoBlock label="Source" value={lead.source} />
                <InfoBlock label="Loan Type" value={String(lead.loan_type || '').replace('_', ' ')} />
                <InfoBlock label="Loan Amount" value={formatCurrency(lead.loan_amount)} />
                <InfoBlock label="City / State" value="-" />
                <InfoBlock label="Address" value="-" />
                <InfoBlock label="Assigned User" value={lead.assigned_to_name || '-'} />
                <InfoBlock label="Created By" value={lead.created_by_name || '-'} />
                <InfoBlock label="Created At" value={`${fmtDateTime(lead.created_at).date}, ${fmtDateTime(lead.created_at).time}`} />
                <InfoBlock label="Updated At" value={`${fmtDateTime(lead.updated_at).date}, ${fmtDateTime(lead.updated_at).time}`} />
                <InfoBlock label="Remarks / Notes" value={lead.remarks || '-'} />
              </CardContent>
            </Card>

            <Card className="border border-border">
              <CardHeader>
                <CardTitle>Follow-Up Summary</CardTitle>
                <CardDescription>Current active follow-up and quick actions</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {currentFollowUp ? (
                  <>
                    <div className="rounded-xl border bg-muted/30 p-4 space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold">{currentFollowUp.follow_up_type}</p>
                          <p className="text-xs text-muted-foreground">
                            {fmtDateTime(currentFollowUp.scheduled_at).date} at {fmtDateTime(currentFollowUp.scheduled_at).time}
                          </p>
                        </div>
                        <StatusBadge status={currentFollowUp.status} />
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="outline" className={priorityStyles[currentFollowUp.priority] || ''}>{currentFollowUp.priority}</Badge>
                        <Badge variant="outline">{currentFollowUp.created_by_name || 'Unknown User'}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{currentFollowUp.notes || 'No follow-up note added.'}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" onClick={() => setEditingItem({
                        ...currentFollowUp,
                        follow_up_id: currentFollowUp.id,
                        follow_up_note: currentFollowUp.notes,
                        follow_up_at: currentFollowUp.scheduled_at,
                        lead_name: lead.customer_name,
                      })}>
                        <Edit className="mr-2 h-4 w-4" />
                        Edit Follow-Up
                      </Button>
                      <Button variant="outline" onClick={() => setEditingItem({
                        ...currentFollowUp,
                        follow_up_id: currentFollowUp.id,
                        follow_up_note: currentFollowUp.notes,
                        follow_up_at: currentFollowUp.scheduled_at,
                        lead_name: lead.customer_name,
                      })}>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Reschedule
                      </Button>
                      <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setCompletingItem({
                        ...currentFollowUp,
                        follow_up_id: currentFollowUp.id,
                        follow_up_note: currentFollowUp.notes,
                        follow_up_at: currentFollowUp.scheduled_at,
                        lead_name: lead.customer_name,
                      })}>
                        <CheckCircle2 className="mr-2 h-4 w-4" />
                        Mark Complete
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="rounded-xl border border-dashed p-6 text-sm text-muted-foreground">
                    No active follow-up found for this lead.
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
            <Card className="border border-border">
              <CardHeader>
                <CardTitle>All Follow-Ups</CardTitle>
                <CardDescription>Complete follow-up history for this lead</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Schedule</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Priority</TableHead>
                        <TableHead>Created By</TableHead>
                        <TableHead>Completed At</TableHead>
                        <TableHead>Notes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {followUps.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium">{fmtDateTime(item.scheduled_at).date}</span>
                              <span className="text-xs text-muted-foreground">{fmtDateTime(item.scheduled_at).time}</span>
                            </div>
                          </TableCell>
                          <TableCell>{item.follow_up_type}</TableCell>
                          <TableCell><StatusBadge status={item.status} /></TableCell>
                          <TableCell>
                            <Badge variant="outline" className={priorityStyles[item.priority] || ''}>{item.priority}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col">
                              <span>{item.created_by_name || '-'}</span>
                              <span className="text-xs text-muted-foreground">{item.created_by_role || '-'}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {item.completed_at
                              ? `${fmtDateTime(item.completed_at).date}, ${fmtDateTime(item.completed_at).time}`
                              : '-'}
                          </TableCell>
                          <TableCell className="max-w-[260px] text-sm text-muted-foreground">{item.notes || '-'}</TableCell>
                        </TableRow>
                      ))}
                      {followUps.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="py-12 text-center text-muted-foreground">
                            No follow-ups found for this lead.
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-6">
              <Card className="border border-border">
                <CardHeader>
                  <CardTitle>Notes / Additional Information</CardTitle>
                  <CardDescription>Current lead remarks and captured metadata</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Tag className="mt-0.5 h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Priority</p>
                      <p className="text-sm text-muted-foreground">{lead.priority || '-'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <User className="mt-0.5 h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Assigned Employee / Agent</p>
                      <p className="text-sm text-muted-foreground">{lead.assigned_to_name || '-'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="mt-0.5 h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Address / Location</p>
                      <p className="text-sm text-muted-foreground">Current codebase me address, city aur state fields available nahi hain.</p>
                    </div>
                  </div>
                  <div className="rounded-xl border bg-muted/20 p-4 text-sm text-muted-foreground">
                    {lead.remarks || 'No remarks saved for this lead.'}
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-border">
                <CardHeader>
                  <CardTitle>Timeline</CardTitle>
                  <CardDescription>Lead and follow-up activity from existing system records</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {timeline.map((item) => (
                    <div key={item.id} className="flex gap-3">
                      <div className="mt-1 h-2.5 w-2.5 rounded-full bg-emerald-500 shrink-0" />
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-sm font-semibold">{item.title}</p>
                          <span className="text-xs text-muted-foreground">
                            {fmtDateTime(item.occurred_at).date}, {fmtDateTime(item.occurred_at).time}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">{item.description || '-'}</p>
                        {(item.actor_name || item.actor_role) ? (
                          <p className="mt-1 text-xs text-muted-foreground">
                            {item.actor_name || 'Unknown user'}{item.actor_role ? ` • ${item.actor_role}` : ''}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  ))}
                  {timeline.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No activity timeline available.</p>
                  ) : null}
                </CardContent>
              </Card>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
